import axios from "axios";

import type {
  ApiTypes,
  AttributionData,
  EventType,
  Schemas,
} from "@fluid-app/api-client";
import {
  addCartItems,
  captureLead,
  createCart,
  createCartEnrollment,
  getCart,
  lookupAffiliate,
  removeCartItem,
  retrieveSettings,
  showLibrary,
  showMedia,
  subscribeCartItem,
  unsubscribeCartItem,
  updateCartCountry,
  updateCartLanguage,
} from "@fluid-app/api-client";
import {
  getSessionToken,
  initializeFairshare,
  reset,
  trackCheckoutStartedSync,
} from "@fluid-app/fairshare";
import {
  COOKIE_KEYS,
  getCookie,
  STORAGE_KEYS,
  StorageManager,
} from "@fluid-app/storage-manager";

import type { FluidConfig } from "../types";
import { debug } from "../../../fairshare/src/utils/debug";
import { setApiEndpoint } from "../utils/config";
import { AttributionIntegration } from "./AttributionIntegration";
import { getCountryFromLocale, getLanguageFromLocale } from "./localeUtils";

/**
 * Core implementation of the Fluid SDK
 * Focuses on cart management functionality
 */
export class FluidCore {
  private static instance: FluidCore | null = null;
  private initialized = false;
  private storageManager: StorageManager;

  // Track cart item count
  private cartItemCount = 0;

  // Cart API related state (moved from CartApiClient class)
  private fluidShop = "";

  // Track pending cart operations
  private pendingOperations: Promise<unknown>[] = [];

  // Track settings loading state
  private settingsLoading: Promise<
    ApiTypes["settings-retrieve"]["response"] | null
  > | null = null;

  // Attribution integration system
  private attributionIntegration: AttributionIntegration | null = null;

  // Custom checkout handler
  private onCheckout?: () => void | Promise<void>;

  private constructor() {
    // Initialize storage manager
    this.storageManager = new StorageManager();
  }

  private dispatchEvent = (
    eventName: EventType,
    data?: { cart?: Schemas["Cart"] },
  ) => {
    const eventDetail = data ? { ...data } : {};
    window.dispatchEvent(
      new CustomEvent(eventName as string, { detail: eventDetail }),
    );
  };

  private getCartToken(): string | null {
    const cart = this.storageManager.getObject<Schemas["Cart"]>({
      key: STORAGE_KEYS.CART,
    });
    return cart?.cart_token ?? null;
  }

  /**
   * Create a new cart
   * @param request Cart creation request data
   * @returns Promise resolving to the cart creation response
   */
  private async createCart(
    request: Parameters<typeof createCart>[number]["request"],
  ): Promise<ApiTypes["cart-create"]["response"]> {
    console.log("Creating cart", request);
    this.ensureInitialized();

    try {
      const jwt = this.storageManager.getItem({
        key: COOKIE_KEYS.AUTHENTICATED_JWT,
      });

      // Create cart via API
      const response = await createCart({
        request,
        query: {
          jwt: jwt ?? undefined,
        },
      });

      if (response.cart?.cart_token) {
        this.saveCartToken(response.cart.cart_token);
      }

      // Save cart data if available
      if (response.cart) {
        this.saveCart(response.cart);
        this.dispatchEvent("CREATE_CART", { cart: response.cart });
      } else {
        this.dispatchEvent("CREATE_CART");
      }

      return response;
    } catch (error) {
      console.error("Error creating cart", error);
      throw error;
    }
  }

  async updateCartItems(
    items: ApiTypes["cart_items-add"]["request"]["items"],
    countryCode?: string,
  ): Promise<void> {
    this.ensureInitialized();

    const cartToken = this.getCartToken();
    if (!cartToken) {
      throw new Error("No cart token available. Create a cart first.");
    }

    // Get current cart from local storage for optimistic updates
    const currentCart = this.getLocalCart();
    if (currentCart?.items) {
      // Create an optimistically updated cart
      const updatedItems = [...currentCart.items];

      // Update quantities for existing items or add new items
      for (const item of items) {
        const existingItemIndex = updatedItems.findIndex(
          (i) => i.variant?.id === item.variant_id,
        );

        if (existingItemIndex >= 0) {
          // Update existing item
          updatedItems[existingItemIndex] = {
            ...updatedItems[existingItemIndex],
            quantity: item.quantity,
          };
        }
      }

      // Save optimistically updated cart to local storage
      this.saveCart({
        ...currentCart,
        items: updatedItems,
      });

      // Dispatch event for optimistic update
      this.dispatchEvent("UPDATE_CART_ITEM");
    }

    // Make the actual API call and track it as a pending operation
    const operation = this.addCartItems({
      items,
      countryCode,
      event: "UPDATE_CART_ITEM",
    });

    // Add this operation to pending operations
    this.addPendingOperation(operation);

    // Wait for the operation to complete
    await operation;
  }

  /**
   * Add items to the cart
   * @param options The options for adding items to cart
   * @returns Promise resolving to the updated cart
   */
  async addCartItems({
    items,
    countryCode,
    event = "ADD_TO_CART",
  }: {
    items: (ApiTypes["cart_items-add"]["request"]["items"][0] & {
      subscribe?: boolean;
      subscription_plan_id?: number;
    })[];
    countryCode?: string;
    event?: EventType;
  }): Promise<ApiTypes["cart_items-add"]["response"]["cart"]> {
    this.ensureInitialized();

    // Get country code from parameter, stored value, or default
    const effectiveCountryCode =
      countryCode ??
      getCountryFromLocale(
        this.storageManager.getItem({ key: COOKIE_KEYS.LOCALE }),
      );

    // Process items to ensure each has a quantity (default to 1 if not specified)
    // Also forward subscription_plan_id if present
    const processedItems = items.map((item) => ({
      variant_id: item.variant_id,
      quantity: item.quantity || 1,
      subscribe: !!item.subscription_plan_id,
      subscription_plan_id: item.subscription_plan_id,
    }));

    // Process subscription preferences from items
    const itemsWithSubscription = items.filter(
      (item) => item.subscribe === true || !!item.subscription_plan_id,
    );

    // Check if cart token exists
    let cartToken = this.getCartToken();

    // If no cart token, create a new cart first
    if (!cartToken) {
      console.log("No cart token found, creating new cart");
      try {
        // Create a new cart with these items
        const createCartResponse = await this.createCart({
          country_code: effectiveCountryCode,
          items: processedItems,
          ...this.buildMetadata(),
        });

        // Handle subscription preferences after cart creation
        if (createCartResponse.cart && itemsWithSubscription.length > 0) {
          cartToken = createCartResponse.cart.cart_token ?? "";
          if (cartToken) {
            // Process subscriptions for newly created cart
            for (const item of itemsWithSubscription) {
              const cartItem = createCartResponse.cart.items?.find(
                (i) => i.variant?.id === item.variant_id,
              );
              if (cartItem?.id) {
                await this._setItemSubscription(
                  cartItem.id,
                  true,
                  item.subscription_plan_id,
                );
              } else {
                console.warn(
                  "[FluidCore] Could not find cart item for variant_id (creation):",
                  item.variant_id,
                );
              }
            }
          }
        }

        // Return the newly created cart
        if (createCartResponse.cart) {
          return createCartResponse.cart;
        }
      } catch (e) {
        const err = e as Error;
        console.error("Error creating cart", err);
        throw new Error("Failed to create a cart");
      }

      // Check again for cart token after creation attempt
      cartToken = this.getCartToken();
      if (!cartToken) {
        throw new Error("Failed to create a cart - no cart token available");
      }
    }

    // Get current cart from local storage for optimistic updates
    const currentCart = this.getLocalCart();
    if (currentCart?.items) {
      // Create an optimistically updated cart
      const updatedItems = [...currentCart.items];

      // Add or update items in the cart
      for (const newItem of processedItems) {
        // Only find regular (non-enrollment) items with the matching variant_id
        // This prevents updating enrollment pack items when adding regular items
        const existingIndex = updatedItems.findIndex(
          (item) => item.variant?.id === newItem.variant_id && !item.enrollment,
        );

        if (existingIndex >= 0) {
          // Update existing item quantity
          updatedItems[existingIndex] = {
            ...updatedItems[existingIndex],
            quantity: newItem.quantity,
          };
        }
      }

      // Save the optimistically updated cart locally
      this.saveCart({
        ...currentCart,
        items: updatedItems,
      });
    }

    try {
      // Dispatch the appropriate event
      this.dispatchEvent(event);

      // Make the API call to add the items
      const response = await addCartItems({
        params: { cart_token: cartToken },
        request: { items: processedItems, metadata: this.buildMetadata() },
      });

      // Save the cart with full data from the API
      if (response.cart) {
        this.saveCart(response.cart);

        // Process subscription preferences for newly added items
        if (itemsWithSubscription.length > 0) {
          for (const item of itemsWithSubscription) {
            const cartItem = response.cart.items?.find(
              (i) => i.variant?.id === item.variant_id,
            );
            if (cartItem?.id) {
              await this._setItemSubscription(
                cartItem.id,
                true,
                item.subscription_plan_id,
              );
            }
          }
        }
      }

      return response.cart;
    } catch (e) {
      const err = e as Error;
      console.error("Error adding items to cart", err);
      throw new Error("Failed to add items to cart");
    }
  }

  /**
   * Helper method to generate common query parameters
   * @returns Object with common query parameters
   */
  private getCommonQueryParams() {
    return {
      fluid_shop: this.fluidShop,
      fluid_session: this.getSessionToken(),
    };
  }

  /**
   * Get the current cart
   * @returns Promise resolving to the cart response
   */
  async getCart(): Promise<ApiTypes["cart-get"]["response"]["cart"]> {
    this.ensureInitialized();

    const cartToken = this.getCartToken();
    // Check if we have a cart token
    if (!cartToken) {
      return undefined;
    }

    try {
      // Get cart via API
      const response = await getCart({
        params: {
          cart_token: cartToken,
        },
        query: this.getCommonQueryParams(),
      });

      // Save cart data if available
      if (response.cart) {
        this.saveCart(response.cart);
      }

      return response.cart;
    } catch (error) {
      console.error("Error getting cart", error);

      // Check if this is an axios error with a 410 status code (Gone - cart has been submitted)
      if (axios.isAxiosError(error) && error.response?.status === 410) {
        console.log(
          "Cart has been submitted and is no longer available. Clearing cart token.",
        );
        this.clearCart();
        return undefined;
      }

      throw error;
    }
  }

  public static getInstance(): FluidCore {
    FluidCore.instance ??= new FluidCore();
    return FluidCore.instance;
  }

  /**
   * Set custom storage manager
   * @param storageManager Custom storage manager implementation
   */
  public setStorageManager(storageManager: StorageManager): void {
    this.storageManager = storageManager;
  }

  /**
   * Get the current storage manager instance
   * @returns The current storage manager
   */
  public getStorageManager(): StorageManager {
    return this.storageManager;
  }

  /**
   * Set the fluidShop identifier for API requests
   * @param fluidShop - The identifier for the shop
   */
  public setFluidShop(fluidShop: string): void {
    if (!fluidShop) {
      throw new Error("fluidShop cannot be empty");
    }
    this.fluidShop = fluidShop;
  }

  public initializeFluid(config: FluidConfig): void {
    if (this.initialized) {
      return;
    }

    // Validate that fluidShop is provided
    if (!config.fluidShop) {
      throw new Error("fluidShop is required in the configuration");
    }

    // Set custom API endpoint if provided
    if (config.apiEndpoint) {
      // Import and use the setApiEndpoint function
      setApiEndpoint(config.apiEndpoint);
    }

    // Store the configuration in localStorage for other components to access
    if (typeof window !== "undefined") {
      this.storageManager.setObject({
        key: STORAGE_KEYS.SETTINGS_OVERRIDE,
        value: config,
      });
    }

    // Check for country and locale in cookies and store in localStorage
    if (typeof window !== "undefined") {
      try {
        // Check for country code in cookies
        const locale = getCookie(COOKIE_KEYS.LOCALE);
        const countryCode = getCountryFromLocale(locale);
        const language = getLanguageFromLocale(locale);
        if (locale) {
          this.storageManager.setItem({
            key: COOKIE_KEYS.LOCALE,
            value: locale,
          });
        }

        if (countryCode) {
          this.storageManager.setItem({
            key: COOKIE_KEYS.COUNTRY,
            value: countryCode,
          });
        }

        if (language) {
          this.storageManager.setItem({
            key: COOKIE_KEYS.LANGUAGE,
            value: language,
          });
        }
      } catch (error) {
        console.error("Error reading country/locale cookies:", error);
      }
    }

    // Set the fluidShop before initializing FairShare
    this.setFluidShop(config.fluidShop);

    // Mark as initialized
    this.initialized = true;

    // Initialize cart item count
    const cart = this.getLocalCart();
    if (cart) {
      this.updateCartItemCount(cart);
    }

    // Initialize FairShare and check cart status after initialization
    try {
      // initializeFairshare now returns a Promise
      const fairsharePromise: Promise<void> = initializeFairshare(config);

      // Check if we need to validate a cart
      if (typeof window !== "undefined") {
        const cartToken = this.getCartToken();
        if (cartToken) {
          // Use void to safely handle the promise chain
          void (async () => {
            try {
              await fairsharePromise;
              await this.getCart();
            } catch {
              // Errors already handled in getCart
            }
          })();
        }
      }

      // Initialize attribution integration after FairShare initialization
      fairsharePromise
        .then(async () => {
          // Initialize attribution integration
          this.attributionIntegration = new AttributionIntegration(this);

          // Initialize attribution integration with attribution override if provided
          await this.attributionIntegration.initialize(
            config.attributionOverride,
          );
        })
        .catch((error: unknown) => {
          console.error(
            "Error during FairShare initialization, skipping attribution integration:",
            error instanceof Error ? error.message : String(error),
          );
        })
        .finally(() => {
          void (async () => {
            // Check if settings were successfully loaded by attribution integration
            // If not, fetch them as a fallback to ensure settings are always available
            let settings = this.getSettings();
            if (!settings) {
              console.log(
                "No settings found after attribution integration, fetching as fallback",
              );
              settings = await this.fetchSettings().catch((error) => {
                console.error(
                  "Error fetching fallback settings after FairShare initialization:",
                  error instanceof Error ? error.message : String(error),
                );
                return null;
              });
            }
            // Inject scripts if available
            if (settings?.settings?.scripts) {
              await this.injectScripts(settings.settings.scripts);
            }
          })();
        });
    } catch (error: unknown) {
      console.error(
        "Error initializing FairShare:",
        error instanceof Error ? error.message : String(error),
      );
      // Continue even if FairShare initialization fails
    }
  }

  private ensureInitialized(): void {
    if (!this.initialized) {
      throw new Error("Fluid SDK not initialized. Call init() first.");
    }
  }

  /**
   * Save cart data to storage
   */
  private saveCart(cart: Schemas["Cart"]): void {
    try {
      this.storageManager.setObject<Schemas["Cart"]>({
        key: STORAGE_KEYS.CART,
        value: cart,
      });

      // Update cart item count when saving cart
      this.updateCartItemCount(cart);
    } catch (error) {
      console.error("Error saving cart", error);
    }
  }

  /**
   * Save cart token to storage
   */
  private saveCartToken(token: string): void {
    this.storageManager.setObject<Schemas["Cart"]>({
      key: STORAGE_KEYS.CART,
      value: {
        ...this.storageManager.getObject<Schemas["Cart"]>({
          key: STORAGE_KEYS.CART,
        }),
        cart_token: token,
        attribution: null, // TODO: Get this from the API response
        recurring: [],
      },
    });
  }

  // Cart Management Methods
  public clearCart(): void {
    this.ensureInitialized();
    this.storageManager.removeItem({ key: STORAGE_KEYS.CART });

    // Reset cart item count
    this.cartItemCount = 0;
    this.updateCartCountElements();
  }

  public reset(): void {
    void reset();
    this.clearCart();
    this.initialized = false;
  }

  // Method to add a pending operation
  private addPendingOperation(operation: Promise<unknown>): void {
    const trackedOperation = operation.catch((error) => {
      console.error("Cart operation failed:", error);
    });

    // Add to pending operations
    this.pendingOperations.push(trackedOperation);

    // Clean up completed operations
    void trackedOperation.finally(() => {
      // Safely remove the operation from the array
      const index = this.pendingOperations.indexOf(trackedOperation);
      if (index !== -1) {
        // Create a new array without the completed operation
        this.pendingOperations = this.pendingOperations.filter(
          (op) => op !== trackedOperation,
        );
      }
    });
  }

  public async checkout(): Promise<void> {
    this.ensureInitialized();

    try {
      // Wait for all pending cart operations to complete
      if (this.pendingOperations.length > 0) {
        console.log(
          `Waiting for ${this.pendingOperations.length} pending cart operations to complete...`,
        );
        // Create a copy of the array to avoid linter warning
        const pendingOps = this.pendingOperations.slice();
        await Promise.all(pendingOps);
      }

      // Get the latest cart data to ensure checkout has the most recent state
      try {
        const freshCart = await this.getCart();
        if (freshCart) {
          this.saveCart(freshCart);
        }
      } catch (error) {
        console.error(
          "Error refreshing cart before checkout:",
          error instanceof Error ? error.message : String(error),
        );
        // Continue with checkout even if refresh fails
      }

      // Get cart token
      const cartToken = this.getCartToken();
      if (!cartToken) {
        throw new Error("No cart token available. Create a cart first.");
      }

      // Get attribution from the attribution integration (server-side)
      let attributionData: AttributionData;

      if (this.attributionIntegration) {
        const attribution =
          this.attributionIntegration.getEffectiveAttribution();
        if (attribution) {
          if ("share_guid" in attribution) {
            attributionData = { share_guid: attribution.share_guid };
          } else if ("fluid_rep_id" in attribution) {
            attributionData = { fluid_rep_id: attribution.fluid_rep_id };
          } else if ("email" in attribution) {
            attributionData = { email: attribution.email };
          } else if ("username" in attribution) {
            attributionData = { username: attribution.username };
          } else if ("external_id" in attribution) {
            attributionData = { external_id: attribution.external_id };
          }
        }
      }

      console.log("Attribution data for checkout:", attributionData);

      // Track the checkout event
      console.log(
        "FluidCore: About to send CHECKOUT_STARTED event with data:",
        {
          cartToken,
          fluidShop: this.fluidShop,
          sessionToken: this.getSessionToken(),
          attribution: attributionData,
        },
      );

      // Send checkout started event and wait for API response before redirecting
      console.log("FluidCore: Sending CHECKOUT_STARTED event to API");
      try {
        await trackCheckoutStartedSync({
          cart_token: cartToken,
        });
        console.log(
          "FluidCore: CHECKOUT_STARTED event sent successfully to API",
        );
      } catch (eventError) {
        console.error(
          "FluidCore: Error sending CHECKOUT_STARTED event to API:",
          eventError,
        );
        // Don't let event tracking errors block checkout
      }

      // Redirect to checkout page
      console.log("FluidCore: Redirecting to checkout page");
      const onCheckout = this.getOnCheckout();
      if (onCheckout) {
        await onCheckout();
      } else {
        window.location.href = `https://checkout.fluid.app/checkouts/${cartToken}`;
      }
    } catch (error) {
      console.error(
        "Error during checkout:",
        error instanceof Error ? error.message : String(error),
      );
      throw error;
    }
  }

  /**
   * Remove an item from the cart
   * @param itemId The ID of the item to remove
   * @returns Promise that resolves when the item is removed
   */
  async removeCartItemById(itemId: number): Promise<void> {
    this.ensureInitialized();

    const cartToken = this.getCartToken();
    if (!cartToken) {
      throw new Error("No cart token available. Create a cart first.");
    }

    // Get current cart from local storage for optimistic updates
    const currentCart = this.getLocalCart();
    if (currentCart?.items) {
      // Create an optimistically updated cart by filtering out the item to remove
      const updatedItems = currentCart.items.filter(
        (item) => item.id !== itemId,
      );

      // Save optimistically updated cart to local storage
      this.saveCart({
        ...currentCart,
        items: updatedItems,
      });

      // Dispatch event for optimistic update
      this.dispatchEvent("REMOVE_FROM_CART");
    }

    // Create and track the operation
    const operation = (async () => {
      try {
        // Remove the item via API
        await removeCartItem({
          params: {
            cart_token: cartToken,
            item_id: itemId.toString(),
          },
          query: this.getCommonQueryParams(),
        });

        // Get updated cart to update totals
        const updatedCart = await this.getCart();
        if (updatedCart) {
          this.saveCart(updatedCart);
        }

        // We already dispatched the event for optimistic update
        return;
      } catch (error) {
        console.error("Error removing cart item", error);

        // Check if this is a 404 error specifically for enrollment packs
        const is404Error =
          error instanceof Error &&
          (error.message.includes("404") ||
            error.message.includes("not found"));

        if (is404Error) {
          // For 404 errors, the item might already be removed or doesn't exist
          // Don't clear the entire cart, just refresh to get the current state
          console.warn(
            `Cart item ${itemId} not found (404). It may have already been removed.`,
          );

          try {
            const freshCart = await this.getCart();
            if (freshCart) {
              this.saveCart(freshCart);
            }
          } catch (refreshError) {
            console.error("Error refreshing cart after item 404", refreshError);
            // Only clear cart if we can't refresh it at all
            if (
              refreshError instanceof Error &&
              refreshError.message.includes("404")
            ) {
              this.clearCart();
            }
          }

          // Don't re-throw 404 errors - treat as success
          return;
        }

        // For other errors, refresh cart from server to ensure consistency
        try {
          const freshCart = await this.getCart();
          if (freshCart) {
            this.saveCart(freshCart);
          }
        } catch (refreshError) {
          console.error(
            "Error refreshing cart after item removal failure",
            refreshError,
          );

          // Only clear cart if the cart itself is not found
          if (
            refreshError instanceof Error &&
            refreshError.message.includes("404")
          ) {
            this.clearCart();
          }
        }

        // Re-throw non-404 errors
        throw error;
      }
    })();

    // Add this operation to pending operations
    this.addPendingOperation(operation);

    // Return the operation promise
    return operation;
  }

  /**
   * Get the cart data from local storage without making an API call
   * This is useful for optimistic updates
   * @returns The cart data from local storage or null if not found
   */
  public getLocalCart(): Schemas["Cart"] | null {
    // Check initialization status but don't throw an error
    // This allows components to safely try to get the cart before initialization
    if (!this.initialized) {
      console.warn(
        "Fluid SDK not fully initialized yet when calling getLocalCart",
      );
      return null;
    }

    try {
      // Get cart from local storage
      const cart = this.storageManager.getObject<Schemas["Cart"]>({
        key: STORAGE_KEYS.CART,
      });

      // Update cart count when getting from storage
      if (cart && this.cartItemCount === 0) {
        this.updateCartItemCount(cart);
      }

      return cart;
    } catch (error) {
      console.error("Error getting cart from local storage", error);
      return null;
    }
  }

  /**
   * Build metadata for API requests
   * @returns Metadata object with session, attribution, and other data
   */
  private buildMetadata(
    useCachedAttribution?: boolean,
  ): Schemas["RequestMetadata"] {
    const sessionToken = this.getSessionToken();
    const metadata: Schemas["RequestMetadata"] = {
      fluid_session: sessionToken,
      fluid_shop: this.fluidShop,
    };
    if (this.attributionIntegration) {
      const attribution = this.attributionIntegration.getEffectiveAttribution(
        undefined,
        useCachedAttribution ?? false,
      );
      if (attribution) {
        metadata.attribution = attribution;
      }
    }
    // Add fluid_locale if available
    const locale = this.storageManager.getItem({ key: COOKIE_KEYS.LOCALE });
    if (locale) {
      metadata.fluid_locale = locale;
    }
    // Add fluid_journey if available
    const journey = this.storageManager.getItem({ key: COOKIE_KEYS.JOURNEY });
    if (journey) {
      metadata.fluid_journey = journey;
    }
    return metadata;
  }

  /**
   * Increment the quantity of an item in the cart, or add it if it doesn't exist.
   * @param itemsToIncrement The items to increment with their variant IDs, quantities, and subscription preferences
   * @returns Promise resolving to the updated cart, or undefined if the operation couldn't be completed.
   */
  async incrementCartItem(
    itemsToIncrement: {
      variant_id: number;
      quantity: number;
      subscribe?: boolean;
      subscription_plan_id?: number;
    }[],
  ): Promise<Schemas["Cart"] | undefined> {
    this.ensureInitialized(); // Ensure SDK is initialized

    // Aggregate increments per variantId
    const incrementsMap = new Map<
      number,
      { quantity: number; subscribe?: boolean; subscription_plan_id?: number }
    >();
    for (const item of itemsToIncrement) {
      if (!item.variant_id || !item.quantity || item.quantity <= 0) {
        console.warn("Skipping invalid item in incrementCartItem:", item);
        continue;
      }

      const currentIncrement = incrementsMap.get(item.variant_id);
      incrementsMap.set(item.variant_id, {
        quantity: (currentIncrement?.quantity ?? 0) + item.quantity,
        subscribe: item.subscribe ?? currentIncrement?.subscribe,
        subscription_plan_id:
          item.subscription_plan_id ?? currentIncrement?.subscription_plan_id,
      });
    }

    if (incrementsMap.size === 0) {
      console.warn("No valid items to increment.");
      return undefined;
    }

    // Get current cart state
    const currentCart = this.getLocalCart();

    // Calculate final quantities to set
    const finalItemsToSet: {
      variant_id: number;
      quantity: number;
      subscribe?: boolean;
      subscription_plan_id?: number;
    }[] = [];
    for (const [
      variantId,
      { quantity: totalIncrement, subscribe, subscription_plan_id },
    ] of Array.from(incrementsMap.entries())) {
      // Only find individual (non-enrollment) items with the matching variant_id
      // This prevents adding enrollment pack quantities to individual item quantities
      const currentItem = currentCart?.items?.find(
        (item) => item.variant?.id === variantId && !item.enrollment,
      );
      const currentQuantity = currentItem
        ? Number(currentItem.quantity) || 0
        : 0;
      const newQuantity = currentQuantity + totalIncrement;
      finalItemsToSet.push({
        variant_id: variantId,
        quantity: newQuantity,
        subscribe,
        subscription_plan_id: subscription_plan_id,
      });
    }

    // Always call addCartItems with the final calculated quantities.
    // It handles cart creation and updates internally.
    try {
      const updatedCart = await this.addCartItems({
        items: finalItemsToSet,
        event: "UPDATE_CART_ITEM", // Use UPDATE_CART_ITEM event type
      });
      return updatedCart;
    } catch (error) {
      console.error(
        `Error in incrementCartItem (via addCartItems) for items:`,
        finalItemsToSet,
        error,
      );
      // addCartItems should handle its own optimistic rollbacks if necessary
      return undefined; // Indicate failure
    }
  }

  /**
   * Decrement the quantity of an item in the cart, removing it if the quantity reaches zero or less.
   * @param variantId The variant ID of the item to decrement.
   * @param quantityToRemove The quantity to remove (defaults to 1).
   * @returns Promise resolving when the operation is complete. It might resolve to undefined if the item wasn't found.
   */
  async decrementCartItem(
    variantId: number,
    quantityToRemove = 1,
  ): Promise<Schemas["Cart"] | undefined> {
    this.ensureInitialized();
    const cartToken = this.getCartToken(); // Ensure we have a cart context
    if (!cartToken) {
      console.warn("Cannot decrement item: No cart token available.");
      return undefined;
    }
    if (quantityToRemove <= 0) {
      console.warn("Cannot decrement item: quantityToRemove must be positive.");
      return undefined;
    }

    const currentCart = this.getLocalCart();
    // Only find individual (non-enrollment) items with the matching variant_id
    // This prevents modifying enrollment pack items when decrementing individual items
    const currentItem = currentCart?.items?.find(
      (item) => item.variant?.id === variantId && !item.enrollment,
    );

    if (!currentItem) {
      console.warn(
        `Cannot decrement item ${variantId}: Item not found in cart.`,
      );
      return undefined; // Item not in cart, nothing to do
    }

    const currentQuantity = Number(currentItem.quantity) || 0;
    const newQuantity = currentQuantity - quantityToRemove;

    try {
      if (newQuantity > 0) {
        // If quantity is still positive, update it using addCartItems
        const updatedCart = await this.addCartItems({
          items: [{ variant_id: variantId, quantity: newQuantity }],
          event: "UPDATE_CART_ITEM", // Or consider a new event type?
        });
        return updatedCart;
      } else {
        // If quantity is zero or less, remove the item using its cart item ID
        const itemId = currentItem.id; // Get the specific item ID for removal
        if (itemId === undefined) {
          // Simplified check
          console.error(
            `Cannot remove item with variant ID ${variantId}: Missing item ID.`,
          );
          return undefined;
        }
        await this.removeCartItemById(itemId); // This handles its own API call and optimistic update
        // removeCartItem doesn't return the cart, maybe fetch it after?
        // For now, return undefined as the item is gone or state is handled by removeCartItem's saveCart
        const updatedCart = this.getLocalCart(); // Return the cart state after removal
        return updatedCart ?? undefined; // Convert null to undefined
      }
    } catch (error) {
      console.error(`Error decrementing item ${variantId}:`, error);
      // Existing methods should handle rollbacks
      return undefined; // Indicate failure
    }
  }

  /**
   * Add an enrollment pack to the cart
   * @param enrollmentPackId The ID of the enrollment pack to add
   * @param event Event type to dispatch (defaults to ADD_TO_CART)
   * @returns Promise resolving to the updated cart
   */
  async addEnrollmentPack({
    enrollmentPackId,
    event = "ADD_TO_CART",
  }: {
    enrollmentPackId: number;
    event?: EventType;
  }): Promise<Schemas["Cart"] | undefined> {
    this.ensureInitialized();

    // Check if cart token exists
    let cartToken = this.getCartToken();
    let isNewCart = false;

    // If no cart token, create a new cart first
    if (!cartToken) {
      console.log("No cart token found, creating new cart");
      isNewCart = true;
      try {
        // Create a new cart first, then add the enrollment pack
        const metadata = this.buildMetadata();
        const createCartResponse = await this.createCart({
          country_code: getCountryFromLocale(
            this.storageManager.getItem({ key: COOKIE_KEYS.LOCALE }),
          ),
          ...metadata,
        });

        // Get the cart token from the response
        if (createCartResponse.cart?.cart_token) {
          cartToken = createCartResponse.cart.cart_token;
        } else {
          throw new Error("Failed to get cart token from new cart");
        }
      } catch (e) {
        const err = e as Error;
        console.error("Error creating cart before adding enrollment pack", err);
        throw new Error("Failed to create a cart for enrollment pack");
      }
    }

    // Create and track the operation
    const operation = (async () => {
      try {
        // Make the API call to add the enrollment pack
        const response = await createCartEnrollment({
          params: { cart_token: cartToken },
          request: {
            enrollment_pack_id: enrollmentPackId,
            metadata: this.buildMetadata(),
          },
        });

        // Save the cart with full data from the API
        if (response.cart) {
          this.saveCart(response.cart);

          // Dispatch the appropriate event with cart data
          if (isNewCart) {
            // For new carts, dispatch CREATE_CART with cart data
            this.dispatchEvent("CREATE_CART", { cart: response.cart });
          } else {
            // For existing carts, dispatch the specified event with cart data
            this.dispatchEvent(event, { cart: response.cart });
          }
        }

        return response.cart;
      } catch (e) {
        const err = e as Error;
        console.error("Error adding enrollment pack to cart", err);

        // Refresh the cart to ensure consistency
        try {
          const freshCart = await this.getCart();
          if (freshCart) {
            this.saveCart(freshCart);
            return freshCart;
          }
        } catch (refreshError) {
          console.error(
            "Error refreshing cart after enrollment pack error",
            refreshError,
          );
        }

        throw new Error("Failed to add enrollment pack to cart");
      }
    })();

    // Add this operation to pending operations
    this.addPendingOperation(operation);

    // Return the operation promise
    return operation;
  }

  getCountryCode() {
    return this.storageManager.getItem({ key: COOKIE_KEYS.COUNTRY });
  }

  getLanguage() {
    return this.storageManager.getItem({ key: COOKIE_KEYS.LANGUAGE });
  }

  async setAuthentication(jwt: string) {
    if (typeof jwt !== "string" || !jwt) return;

    // Set JWT and authenticatedAt timestamp
    this.storageManager.setItem({
      key: COOKIE_KEYS.AUTHENTICATED_JWT,
      value: jwt,
    });

    // Store authentication timestamp
    const authenticatedAt = new Date().toISOString();
    this.storageManager.setItem({
      key: COOKIE_KEYS.AUTHENTICATED_AT,
      value: authenticatedAt,
    });

    try {
      const res = await axios.get(`/api/authentication/me?jwt=${jwt}`);
      const data = res.data as Record<string, unknown>;
      const user =
        typeof data.user_company === "object" && data.user_company !== null
          ? data.user_company
          : typeof data.user === "object" && data.user !== null
            ? data.user
            : undefined;
      if (user) {
        this.storageManager.setObject({
          key: STORAGE_KEYS.AUTHENTICATED_USER,
          value: user,
        });
      }
    } catch (err) {
      // Clear both JWT and timestamp if invalid
      this.storageManager.removeItem({ key: COOKIE_KEYS.AUTHENTICATED_JWT });
      this.storageManager.removeItem({ key: COOKIE_KEYS.AUTHENTICATED_AT });
      throw err;
    }
  }

  getAuthenticatedUser() {
    const user = this.storageManager.getItem({
      key: STORAGE_KEYS.AUTHENTICATED_USER,
    });
    console.log("user", user);
    return user;
  }

  /**
   * Set a cart item's subscription status
   * @param itemId The ID of the item to update
   * @param subscribe Whether to subscribe (true) or unsubscribe (false)
   * @returns Promise resolving to the updated cart, or undefined if the operation couldn't be completed
   */
  private async _setItemSubscription(
    itemId: number,
    subscribe: boolean,
    subscriptionPlanId?: number,
  ): Promise<Schemas["Cart"] | undefined> {
    const cartToken = this.getCartToken();
    if (!cartToken) {
      console.error(
        "No cart token available for subscribing/unsubscribing item",
      );
      return undefined;
    }

    // Get current cart from local storage for optimistic updates
    const currentCart = this.getLocalCart();
    if (currentCart?.items) {
      // Find the item to update
      const itemIndex = currentCart.items.findIndex(
        (item) => item.id === itemId,
      );

      if (itemIndex >= 0) {
        // Create a copy of the cart items
        const updatedItems = [...currentCart.items];

        // Update the subscription status
        updatedItems[itemIndex] = {
          ...updatedItems[itemIndex],
          subscription: subscribe,
        };

        // Save optimistically updated cart to local storage
        this.saveCart({
          ...currentCart,
          items: updatedItems,
        });

        // Dispatch event for optimistic update
        this.dispatchEvent("UPDATE_CART_ITEM");
      }
    }

    // Create and track the operation
    const operation = (async () => {
      try {
        // Subscribe or unsubscribe to the item via API
        let response: Awaited<ReturnType<typeof subscribeCartItem>>;

        if (subscribe || subscriptionPlanId) {
          response = await subscribeCartItem({
            params: {
              cart_token: cartToken,
              item_id: itemId.toString(),
            },
            query: {
              subscription_plan_id: subscriptionPlanId,
            },
            request: {
              metadata: this.buildMetadata(),
            },
          });
        } else {
          // Use unsubscribeCartItem from API client
          response = await unsubscribeCartItem({
            params: {
              cart_token: cartToken,
              item_id: itemId.toString(),
            },
            query: this.getCommonQueryParams(),
          });
        }

        // Update local cart with API response
        if (response.cart) {
          this.saveCart(response.cart);
          return response.cart;
        }

        return undefined;
      } catch (error) {
        console.error(
          `Error ${subscribe ? "subscribing to" : "unsubscribing from"} cart item`,
          error,
        );

        // On error, refresh cart from server to ensure consistency
        try {
          const freshCart = await this.getCart();
          if (freshCart) {
            this.saveCart(freshCart);
          }
        } catch (refreshError) {
          console.error(
            `Error refreshing cart after ${subscribe ? "subscription" : "unsubscription"} failure`,
            refreshError,
          );
        }

        throw error;
      }
    })();

    // Add this operation to pending operations
    this.addPendingOperation(operation);

    // Return the operation promise
    return operation;
  }

  /**
   * Subscribe to a cart item
   * @param itemId The ID of the item to subscribe to
   * @param subscriptionPlanId Optional subscription plan ID to pass
   * @returns Promise resolving to the updated cart, or undefined if the operation couldn't be completed
   */
  async subscribeCartItem(
    itemId: number,
    subscriptionPlanId?: number,
  ): Promise<Schemas["Cart"] | undefined> {
    this.ensureInitialized();
    return this._setItemSubscription(itemId, true, subscriptionPlanId);
  }

  /**
   * Unsubscribe from a cart item
   * @param itemId The ID of the item to unsubscribe from
   * @returns Promise resolving to the updated cart, or undefined if the operation couldn't be completed
   */
  async unsubscribeCartItem(
    itemId: number,
  ): Promise<Schemas["Cart"] | undefined> {
    this.ensureInitialized();
    return this._setItemSubscription(itemId, false);
  }

  /**
   * Get the cart item count
   * @returns The current cart item count
   */
  public getCartItemCount(): number {
    return this.cartItemCount;
  }

  /**
   * Update the cart item count based on the cart contents
   * @param cart The cart to use for calculating the count
   */
  private updateCartItemCount(cart: Schemas["Cart"] | null): void {
    if (!cart?.items) {
      this.cartItemCount = 0;
    } else {
      this.cartItemCount = cart.items.reduce(
        (total, item) => total + (Number(item.quantity) || 0),
        0,
      );
    }

    // Update any elements with id "fluid-cart-count"
    this.updateCartCountElements();
  }

  /**
   * Update all elements displaying the cart count (by id, class, or data attribute)
   */
  private updateCartCountElements(): void {
    if (typeof window !== "undefined") {
      // Legacy: update the first element with id 'fluid-cart-count'
      const legacyElement = document.getElementById("fluid-cart-count");
      if (legacyElement) {
        legacyElement.textContent = String(this.cartItemCount);
      }

      // Update all elements with class 'fluid-cart-count'
      const classElements = document.getElementsByClassName("fluid-cart-count");
      for (const el of Array.from(classElements)) {
        (el as HTMLElement).textContent = String(this.cartItemCount);
      }

      // Update all elements with data-fluid-cart-count attribute
      const dataAttrElements = document.querySelectorAll(
        "[data-fluid-cart-count]",
      );
      dataAttrElements.forEach((el) => {
        (el as HTMLElement).textContent = String(this.cartItemCount);
      });
    }
  }

  /**
   * Update locale settings for the cart
   * @param options The locale settings to update
   * @returns void
   */
  async updateLocaleSettings(options: { language?: string; country?: string }) {
    this.ensureInitialized();

    // Validate that at least one parameter is provided
    if (!options.language && !options.country) {
      throw new Error(
        "At least one locale parameter (language or country) must be provided",
      );
    }

    // Validate that parameters are non-empty strings if provided
    if (
      options.language &&
      (!options.language.trim() || typeof options.language !== "string")
    ) {
      throw new Error("Language must be a non-empty string");
    }

    if (
      options.country &&
      (!options.country.trim() || typeof options.country !== "string")
    ) {
      throw new Error("Country must be a non-empty string");
    }

    const cartToken = this.getCartToken();
    // Remove the error throw for missing cart token
    // if (!cartToken) {
    //   throw new Error("No cart token available. Create a cart first.");
    // }

    try {
      // Always update locale in storage and cookies if language or country is provided
      if (options.language || options.country) {
        let language = options.language;
        let country = options.country;

        // If not provided, get from storage
        if (!language) {
          const storedLocale = this.storageManager.getItem({
            key: COOKIE_KEYS.LOCALE,
          });
          language = storedLocale?.split("_")[0] ?? "en";
        }
        country ??= getCountryFromLocale(
          this.storageManager.getItem({ key: COOKIE_KEYS.LOCALE }),
        );

        const newLocale = `${language}_${country}`;
        this.storageManager.setItem({
          key: COOKIE_KEYS.LOCALE,
          value: newLocale,
        });
        this.storageManager.setItem({
          key: COOKIE_KEYS.LANGUAGE,
          value: language,
        });
        this.storageManager.setItem({
          key: COOKIE_KEYS.COUNTRY,
          value: country,
        });
      }

      let updatedCart: Schemas["Cart"] | undefined;

      // Update language if provided and cartToken exists
      if (options.language && cartToken) {
        try {
          const languageResponse = await updateCartLanguage({
            params: { cart_token: cartToken },
            request: {
              language_iso: options.language,
              metadata: this.buildMetadata(),
            },
          });
          updatedCart = languageResponse.cart;
        } catch (error) {
          console.error(
            `Failed to update cart language to '${options.language}':`,
            error,
          );
          throw new Error(
            `Failed to update cart language: ${error instanceof Error ? error.message : String(error)}`,
          );
        }
      }

      // Update country if provided and cartToken exists
      if (options.country && cartToken) {
        try {
          const countryResponse = await updateCartCountry({
            params: { cart_token: cartToken },
            request: {
              country_code: options.country,
              metadata: this.buildMetadata(),
            },
          });
          updatedCart = countryResponse.cart;
        } catch (error) {
          console.error(
            `Failed to update cart country to '${options.country}':`,
            error,
          );
          throw new Error(
            `Failed to update cart country: ${error instanceof Error ? error.message : String(error)}`,
          );
        }
      }

      // Save the updated cart to local storage if we updated it
      if (updatedCart) {
        this.saveCart(updatedCart);
      }

      return;
    } catch (error) {
      // If the error was already transformed by one of the inner catch blocks, just re-throw it
      if (
        error instanceof Error &&
        error.message.startsWith("Failed to update cart")
      ) {
        throw error;
      }

      // Otherwise, transform the generic error into a more specific one
      console.error("Error updating locale settings:", error);
      throw new Error(
        `Failed to update locale settings: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  /**
   * Capture a lead form submission
   * @param formData Lead capture form data
   * @returns Promise resolving to the lead capture response
   */
  async captureLead(
    formData: ApiTypes["lead-capture"]["request"]["lead"],
  ): Promise<ApiTypes["lead-capture"]["response"]> {
    this.ensureInitialized();

    try {
      // Add metadata to the request
      const request: ApiTypes["lead-capture"]["request"] = {
        lead: formData,
        metadata: this.buildMetadata(),
        page: {
          url: window.location.href,
          title: document.title,
          description:
            document
              .querySelector("meta[name='description']")
              ?.getAttribute("content") ?? undefined,
          image:
            document
              .querySelector("meta[property='og:image']")
              ?.getAttribute("content") ?? undefined,
        },
      };

      // Capture the lead via API
      const response = await captureLead({
        request,
      });

      return response;
    } catch (error) {
      console.error("Error capturing lead", error);
      throw error;
    }
  }

  /**
   * Fetches settings from the API and stores them in local storage
   * @param url Optional URL to fetch settings for (defaults to current page)
   * @returns Promise resolving to the settings or null if the operation failed
   */
  async fetchSettings(
    url?: string,
  ): Promise<ApiTypes["settings-retrieve"]["response"] | null> {
    this.ensureInitialized();
    if (this.settingsLoading) {
      return this.settingsLoading;
    }
    this.settingsLoading = (async () => {
      try {
        const metadata = this.buildMetadata(false);
        const request: ApiTypes["settings-retrieve"]["request"] = {
          metadata,
          page: {
            url:
              url ??
              (typeof window !== "undefined" ? window.location.href : ""),
            title: typeof window !== "undefined" ? document.title : "",
          },
        };
        const settings = await retrieveSettings({ request });
        this.storageManager.setObject({
          key: STORAGE_KEYS.SERVER_SETTINGS,
          value: settings,
        });
        // --- Set cookies and local storage for session, locale, and rep_id ---
        const sessionTokenRS = this.getSessionToken();
        if (sessionTokenRS) {
          this.storageManager.setItem({
            key: COOKIE_KEYS.SESSION,
            value: sessionTokenRS,
          });
        }
        const localeFS = settings.metadata?.fluid_locale;
        if (localeFS) {
          this.storageManager.setItem({
            key: COOKIE_KEYS.LOCALE,
            value: localeFS,
          });
        }
        const fluidAffiliate = settings.settings?.affiliate;
        if (fluidAffiliate) {
          this.storageManager.setObject({
            key: COOKIE_KEYS.AFFILIATE,
            value: fluidAffiliate,
          });
        }
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("SETTINGS_AVAILABLE"));
        }
        this.settingsLoading = null;
        // After fetch: if no override, use server-provided attribution for local logic
        if (this.attributionIntegration) {
          const override = this.attributionIntegration.getAttributionOverride();
          if (!override) {
            // Use server-provided attribution for local logic
            const serverAttribution = settings.settings?.attribution;
            this.attributionIntegration.storeServerAttribution(
              request.page.url,
              serverAttribution,
            );
          }
        }
        return settings;
      } catch (error) {
        console.error("Error loading settings:", error);
        this.settingsLoading = null;
        return null;
      }
    })();
    return this.settingsLoading;
  }

  /**
   * Gets settings from local storage
   * @returns The settings from local storage or null if not found
   */
  getSettings<T = ApiTypes["settings-retrieve"]["response"]>(
    useCachedAttribution?: boolean,
  ): T | null {
    const settings = this.storageManager.getObject<T>({
      key: STORAGE_KEYS.SERVER_SETTINGS,
    });

    // Only apply affiliate replacement logic if useCachedAttribution is true
    if (useCachedAttribution && settings) {
      const typedSettings =
        settings as ApiTypes["settings-retrieve"]["response"];

      // Check if affiliate is null and replace with stored affiliate cookie
      const storedAffiliate = this.storageManager.getObject<{
        fluid_rep_id?: number;
      }>({
        key: COOKIE_KEYS.AFFILIATE,
      });

      if (storedAffiliate?.fluid_rep_id && !typedSettings.settings?.affiliate) {
        // Create a new settings object with the affiliate data replaced
        const updatedSettings = {
          ...typedSettings,
          settings: {
            ...typedSettings.settings,
            affiliate: storedAffiliate,
            attribution: {
              fluid_rep_id: storedAffiliate.fluid_rep_id,
            },
          },
        };
        return updatedSettings as T;
      }
    }

    return settings;
  }

  /**
   * Gets settings from local storage, or fetches them from the API if not available
   * @returns Promise resolving to the settings or null if the operation failed
   */
  async getOrFetchSettings<
    T = ApiTypes["settings-retrieve"]["response"],
  >(): Promise<T | null> {
    // Try to get settings from local storage first
    const settings = this.getSettings<T>();
    if (settings) {
      return settings;
    }

    // If no settings in local storage, fetch from API
    const fetchedSettings = await this.fetchSettings();
    return fetchedSettings as T | null;
  }

  /**
   * Extract attribution data from settings response
   * @param settings Settings response from API
   * @returns Attribution data or null if not found
   */
  extractAttributionFromSettings(
    settings: ApiTypes["settings-retrieve"]["response"] | null,
  ): AttributionData | null {
    debug("FluidCore: Extracting attribution from settings");

    if (!settings?.settings?.attribution) {
      debug("FluidCore: No attribution found in settings response");
      return null;
    }

    const attribution = settings.settings.attribution;
    debug("FluidCore: Found attribution in settings:", attribution);

    // Handle different attribution types from the API
    if ("share_guid" in attribution && attribution.share_guid) {
      debug(
        "FluidCore: Found share_guid in attribution:",
        attribution.share_guid,
      );
      return { share_guid: attribution.share_guid };
    }

    if ("fluid_rep_id" in attribution && attribution.fluid_rep_id) {
      debug(
        "FluidCore: Found fluid_rep_id in attribution:",
        attribution.fluid_rep_id,
      );
      return { fluid_rep_id: attribution.fluid_rep_id };
    }

    if ("email" in attribution && attribution.email) {
      debug("FluidCore: Found email in attribution:", attribution.email);
      return { email: attribution.email };
    }

    if ("username" in attribution && attribution.username) {
      debug("FluidCore: Found username in attribution:", attribution.username);
      return { username: attribution.username };
    }

    if ("external_id" in attribution && attribution.external_id) {
      debug(
        "FluidCore: Found external_id in attribution:",
        attribution.external_id,
      );
      return { external_id: attribution.external_id };
    }

    debug("FluidCore: No recognized attribution fields found");
    return null;
  }

  private getSessionToken(): string {
    // Use the getSessionToken that's already imported from fairshare
    return getSessionToken();
  }

  /**
   * Look up affiliate information by attribution data
   * @param attribution The attribution object containing the lookup criteria (email, username, share_guid, fluid_rep_id, or external_id)
   * @returns Promise resolving to the affiliate lookup response
   */
  async lookupAffiliate(
    attribution: Schemas["Attribution"],
  ): Promise<ApiTypes["affiliate-lookup"]["response"]> {
    this.ensureInitialized();

    try {
      // Build the request metadata
      const metadata = this.buildMetadata();

      const request: ApiTypes["affiliate-lookup"]["request"] = {
        metadata: {
          ...metadata,
          attribution,
        },
      };

      // Make the API call
      const response = await lookupAffiliate({ request });

      return response;
    } catch (error) {
      console.error("Error looking up affiliate:", error);
      throw error;
    }
  }

  /**
   * Get library/playlist data by ID or slug
   * @param libraryId The library ID or slug to retrieve
   * @returns Promise resolving to the library response
   */
  async getLibrary(
    libraryId: string,
  ): Promise<ApiTypes["library-show"]["response"]> {
    this.ensureInitialized();

    try {
      const response = await showLibrary({
        request: {
          library: { id: libraryId },
          metadata: this.buildMetadata(),
        },
      });

      return response;
    } catch (error) {
      console.error("Error fetching library", error);
      throw error;
    }
  }

  /**
   * Get media data by ID or slug
   * @param mediaId The media ID or slug to retrieve
   * @returns Promise resolving to the media response
   */
  async getMedia(mediaId: string): Promise<ApiTypes["media-show"]["response"]> {
    this.ensureInitialized();

    try {
      const response = await showMedia({
        request: {
          media: { id: mediaId },
          metadata: this.buildMetadata(),
        },
      });

      return response;
    } catch (error) {
      console.error("Error fetching media", error);
      throw error;
    }
  }

  async injectScripts(
    scripts: NonNullable<
      ApiTypes["settings-retrieve"]["response"]["settings"]
    >["scripts"],
  ) {
    if (location.hostname.includes("localhost")) {
      scripts = [
        {
          src: "/public/cart.js",
        },
        {
          src: "/public/chat.js",
        },
      ];
    }
    if (location.hostname.includes("tacobell.fluid.app")) {
      scripts = [
        {
          src: "https://assets.fluid.app/scripts/fluid-sdk/staging/web-widgets/cart.js",
        },
        {
          src: "https://assets.fluid.app/scripts/fluid-sdk/staging/web-widgets/chat.js",
        },
      ];
    }
    if (!scripts) return;
    // Dynamically inject each widget script using the new PageScriptSettings schema
    const scriptPromises: Promise<void>[] = [];
    const filteredScripts = scripts.filter((script) => !!script.src);
    for (const script of filteredScripts) {
      scriptPromises.push(
        new Promise<void>((resolve, reject) => {
          const s = document.createElement("script");
          s.src = script.src ?? "";
          s.async = true;
          if (script.id && typeof script.id === "string") {
            s.id = script.id;
          }
          s.onload = () => resolve();
          s.onerror = () => {
            console.error(`Failed to load widget script: ${script.src}`);
            reject(new Error(`Failed to load widget script: ${script.src}`));
          };
          document.head.appendChild(s);
        }),
      );
    }
    await Promise.all(scriptPromises);
  }

  /**
   * Set a custom checkout handler to be called when checkout is triggered from the CartWidget.
   * @param fn The handler function (can be async or sync)
   */
  public setOnCheckout(fn: () => void | Promise<void>): void {
    this.onCheckout = fn;
  }

  /**
   * Get the currently registered custom checkout handler, if any.
   */
  public getOnCheckout(): (() => void | Promise<void>) | undefined {
    return this.onCheckout;
  }

  /**
   * Get the checkout URL for the current cart
   * @returns The checkout URL as a string, or undefined if no cart token is present
   */
  public getCheckoutUrl(): string | undefined {
    const cartToken = this.getCartToken();
    if (!cartToken) return undefined;
    return `https://checkout.fluid.app/checkouts/${cartToken}`;
  }
}
